import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { sendWelcomeEmail } from '../lib/email';
import { handleNewUser } from './handle_new_user';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.pathname.startsWith('/shared/') || 
        window.location.pathname.startsWith('/series/') ||
        window.location.pathname.startsWith('/study/')) {
      setLoading(false);
      return;
    }

    // Get initial session
    const initAuth = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        setUser(session?.user ?? null);

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          setUser(session?.user ?? null);
          
          if (event === 'SIGNED_UP' && session?.user) {
            const isOAuthUser = session.user.app_metadata?.provider === 'google';
            if (!isOAuthUser) {
              try {
                await handleNewUser(session.user, false);
                if (import.meta.env.DEV) console.log('User profile created');

                const firstName = session.user.user_metadata.first_name || 'User';
                const selectedTier = session.user.user_metadata.selectedTier || 'Basic';

                await sendWelcomeEmail(
                  session.user.email || '',
                  firstName,
                  selectedTier === 'premium' ? 'Premium' : selectedTier === 'church' ? 'Church' : 'Basic'
                );

                if (import.meta.env.DEV) console.log('Welcome email dispatched');
              } catch (error) {
                console.error('Failed to handle new user signup:', error);
              }
            }
          }
          
          if (event === 'SIGNED_OUT' && 
              !window.location.pathname.startsWith('/shared/') &&
              !window.location.pathname.startsWith('/series/') &&
              !window.location.pathname.startsWith('/study/') &&
              !window.location.pathname.startsWith('/auth')) {
            navigate('/auth');
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize authentication');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [navigate]);

  const signOut = async () => {
    try {
      setLoggingOut(true);
      setError(null);

      // Clear only auth-related keys — preserve user preferences (dark mode, font sizes, etc.)
      const AUTH_KEYS = ['isAdmin', 'isSuperAdmin', 'hasFreePremiun', 'pendingUpgrade',
                         'newGoogleSignup', 'newUserOnboarding', 'welcomeModalShown', 'lastTrialReminderDate'];
      AUTH_KEYS.forEach(key => localStorage.removeItem(key));

      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Navigate to auth page
      navigate('/auth');
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign out');
      
      // Force redirect to auth page even if there's an error
      navigate('/auth');
    } finally {
      setLoggingOut(false);
    }
  };

  const signOutAllDevices = async () => {
    try {
      setLoggingOut(true);
      setError(null);

      // Clear only auth-related keys — preserve user preferences (dark mode, font sizes, etc.)
      const AUTH_KEYS = ['isAdmin', 'isSuperAdmin', 'hasFreePremiun', 'pendingUpgrade',
                         'newGoogleSignup', 'newUserOnboarding', 'welcomeModalShown', 'lastTrialReminderDate'];
      AUTH_KEYS.forEach(key => localStorage.removeItem(key));

      // Sign out from all devices
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
      
      // Navigate to auth page
      navigate('/auth', { replace: true });
    } catch (err) {
      console.error('Sign out all devices error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign out from all devices');
      
      // Force redirect to auth page even if there's an error
      navigate('/auth', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  return {
    user,
    loading,
    loggingOut,
    error,
    signOut,
    signOutAllDevices,
  };
}